import React, { useState } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import Filter from './Documents/filter';
import Results from './Documents/results';
import type { SearchState } from './Documents/types'
import { format, subMonths } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import fitdocApi from '../../../lib/axios';
import { ContentLabel } from '../../helpers/content-label'
import { buildRelnrMetafilter } from './Documents';
import { useSelector } from 'react-redux';

function Documents(){
    const userdata = useSelector((state:any)=>state.userdata);
    const { getValues } = useFormContext<SearchState>()

    const [offset, setOffset] = useState(0);         // Pagination offset
    const [allResults, setAllResults] = useState<any[]>([]); // Accumulated results
    const [hasMore, setHasMore] = useState(false);     // If there are more results to fetch
    const limit = 50;

    const { mutate:search, isPending } = useMutation({
        mutationFn: async (currentOffset:number=0) => {
            const values = getValues();

            let metafilter = await buildRelnrMetafilter();

            const relnrs_mf = values?.relations?.join('|')
            if(relnrs_mf) {
                if(metafilter) { metafilter += `|${relnrs_mf}` }
                else { metafilter = `RELNR=${relnrs_mf}` }
            }
            
            const { data } = await fitdocApi.get('documents/getbyfilter', {
                params: {
                    searchstring: values.searchstring,
                    searchcontent: values.searchcontent ? 1 : 0,
                    doctypes: values.doctypes?.join(';'),
                    startdate: values.startdate ? format(values.startdate, 'yyyyMMdd') : undefined,
                    enddate: values.enddate ? format(values.enddate, 'yyyyMMdd') : undefined,
                    dossiermetafilter: metafilter,
                    offset: currentOffset,
                    limit: limit
                }
            })

            if(data.errorcode!==0)  throw new Error('Error fetching documents');
            if(!Array.isArray(data.object)) throw new Error('Error fetching documents');

            return data.object;
        },
        onSuccess: (newResults) => {
            setAllResults(prev => [...prev, ...newResults]);
            setOffset(prev => prev + limit);
            setHasMore(newResults.length === limit); // If less than limit, assume no more data
        },
        onError: (err) => {
            console.error("error", err)
        }
    })    

    const handleSearch = () => {
        setOffset(0);
        setAllResults([]);
        search(0); // Start from offset 0
    };

    const loadMore = () => {
        if (!isPending && hasMore) {
            search(offset);
        }
    };

    return (
    <div className='flex flex-col gap-4 md:gap-8'>
        <header>
            <ContentLabel path="infofiches.title" className='text-xl font-bold md:text-4xl'/>
        </header>

        <Filter 
            onSearch={()=>{handleSearch()}}
            isPending={isPending}
            hideDoctypes
            hideSearchContent
            hideRelations={userdata?.role?.toString()==='1' ? false : true}
            labels={{
                title: 'infofiches.subtitle',
                description: 'infofiches.description',
                search_button: 'documents.search_button'
            }}
        />

        <Results 
            results={allResults}
            loadMore={loadMore}
            hasMore={hasMore}
            isPending={isPending}
            display='by-week'
        />
    </div>
    )
}

export default () => {
    const form = useForm<SearchState>({
        defaultValues: {
            startdate: subMonths(new Date(), 1),
            enddate: new Date(),
            doctypes: ['INFOFICHE']
        }
    });

    return (
        <FormProvider {...form}>
            <Documents />
        </FormProvider>
    )
}